import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Sparkles, Loader2, Copy } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * iter121 — Poll-vote shareable post.
 *
 * Renders ONE 9:16 scene that mirrors today's poll question + 4 leading
 * options. The card pulls live poll data so it auto-rotates as soon as
 * the weekly rotation moves on, no redeploy needed. Designed for
 * one-tap engagement: screen-record → post → first tap from social
 * lands the visitor straight on the homepage where they can vote.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");

function Sticker({ children, color = "#fff", textColor = "#0e0e0e" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.22em] mono"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function CopyBlock({ title, body, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      toast.success("Caption copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Couldn't copy — long-press to copy manually"); }
  };
  return (
    <div className="bg-[#0e0e0e] text-white border border-[#1f1f1f] p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="font-display text-sm">{title}</div>
        <button
          onClick={copy}
          className="text-xs mono uppercase tracking-wider text-[#ff4500] hover:underline inline-flex items-center gap-1"
        >
          <Copy className="w-3.5 h-3.5" /> {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap font-sans text-xs sm:text-sm text-[#ddd] leading-relaxed">{body}</pre>
    </div>
  );
}

export default function PollVotePost() {
  const [poll, setPoll] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/polls/today");
        if (!cancelled) {
          setPoll(data);
          document.title = `${data.question} · Vote · CampCompass UK`;
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!poll) {
    return (
      <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#ff4500]" />
      </div>
    );
  }

  // Show the top 4 options on the card so the post stays readable at
  // thumbnail size in feeds. Sort by current vote count desc so the
  // leader is visible.
  const topFour = [...(poll.options || [])]
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);

  const captionLong = `🏕 ${poll.question}

Tap the link, pick yours — see live results from the rest of the UK camping community ⬇️

${PUBLIC_URL}

(One tap. No login. One vote per browser — change your mind any time.)

#ukcamping #campingweekend #campingtrip`;

  const captionShort = `${poll.question}
Tap & vote 👉 ${PUBLIC_URL}`;

  const palette = [
    { grad: "linear-gradient(135deg,#0ea5e9 0%,#3b82f6 100%)" },
    { grad: "linear-gradient(135deg,#fb923c 0%,#facc15 100%)" },
    { grad: "linear-gradient(135deg,#7c3aed 0%,#ec4899 100%)" },
    { grad: "linear-gradient(135deg,#16a34a 0%,#0d9488 100%)" },
  ];

  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white pb-24" data-testid="poll-vote-post">

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-6">
        <Link to="/" className="text-xs mono text-[#888] hover:text-[#ff4500] uppercase tracking-wider">← Back</Link>
        <div className="mt-3 inline-flex items-center gap-2 text-[11px] mono uppercase tracking-[0.25em] text-[#ff4500]">
          <Sparkles className="w-3.5 h-3.5" /> Poll vote post · one tap
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2">{poll.question}</h1>
        <p className="text-sm text-[#bbb] mt-2 max-w-xl">
          Screen-record the scene below and post it. The first tap from social lands
          on the homepage where the live poll is sitting — visitors vote in one tap
          and instantly see the matching campsites for their pick.
        </p>
      </div>

      {/* THE CARD — single 9:16 scene */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 flex justify-center">
        <div className="flex flex-col items-center">
          <div className="mb-2 text-[11px] mono uppercase tracking-[0.25em] text-[#888]">Scene · poll snippet</div>
          <section
            className="relative w-[300px] sm:w-[330px] overflow-hidden shadow-2xl text-white"
            style={{ aspectRatio: "9 / 16", background: "linear-gradient(180deg,#0e0e0e 0%,#1a1a1a 70%,#ff4500 100%)" }}
            data-testid="poll-vote-scene"
          >
            <div className="p-4 pt-6 text-center">
              <Sticker color="#ff4500" textColor="#ffffff">
                <MapPin className="w-3 h-3" /> Reader poll · live
              </Sticker>
            </div>
            <div className="px-4 text-center">
              <div className="font-display text-[26px] sm:text-[28px] leading-[1.05] drop-shadow-[0_3px_12px_rgba(0,0,0,0.7)]">
                {poll.question}
              </div>
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#ffe7d6] mt-3">
                {(poll.total || 0).toLocaleString("en-GB")} votes so far
              </div>
            </div>

            {/* Top-4 options preview */}
            <div className="absolute top-[46%] left-3 right-3 space-y-2">
              {topFour.map((opt, i) => {
                const pal = palette[i % palette.length];
                return (
                  <div
                    key={opt.key}
                    className="relative overflow-hidden text-left p-2.5 sm:p-3"
                    style={{ background: pal.grad }}
                  >
                    <div
                      className="absolute inset-y-0 left-0"
                      style={{
                        width: `${Math.max(6, Math.min(100, opt.pct || 0))}%`,
                        background: "rgba(255,255,255,0.18)",
                        mixBlendMode: "overlay",
                      }}
                    />
                    <div className="relative flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[9px] mono uppercase tracking-[0.2em] opacity-80">#{i + 1}</div>
                        <div className="font-display text-[14px] leading-tight truncate">{opt.label}</div>
                      </div>
                      <div className="mono text-[10px] opacity-90 shrink-0">{opt.pct}%</div>
                    </div>
                  </div>
                );
              })}
              {(poll.options || []).length > 4 && (
                <div className="text-center mono text-[10px] uppercase tracking-wider text-white/85 mt-1">
                  + {poll.options.length - 4} more options
                </div>
              )}
            </div>

            <div className="absolute bottom-12 left-3 right-3 text-center">
              <div className="bg-white text-[#0e0e0e] inline-block px-4 py-2.5">
                <div className="font-display text-base leading-tight">Tap once to vote</div>
                <div className="mono text-[10px] uppercase tracking-wider mt-0.5 opacity-70">camp-compass.uk</div>
              </div>
            </div>

            <div
              className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/65 backdrop-blur-sm text-white text-[10px] sm:text-[11px] font-bold mono uppercase tracking-[0.22em] text-center pointer-events-none z-10"
              aria-hidden="true"
            >
              🌐 camp-compass.uk
            </div>
          </section>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-12 space-y-4">
        <Link
          to="/"
          className="block bg-[#ff4500] text-white text-center py-4 font-display text-lg hover:bg-[#0e0e0e] transition-colors"
          data-testid="poll-vote-post-cta-go-vote"
        >
          Go vote now <ArrowRight className="inline w-4 h-4 ml-1" />
        </Link>

        <CopyBlock
          title="Caption · long (Instagram + Facebook)"
          body={captionLong}
          testid="poll-vote-caption-long"
        />
        <CopyBlock
          title="Caption · short (Stories / TikTok)"
          body={captionShort}
          testid="poll-vote-caption-short"
        />
      </div>
    </div>
  );
}
